/**
 * One sponsor at one event, matching screen-sponsor-detail in
 * docs/prototype.html.
 *
 * Two tabs, as in the prototype. Overview carries the things that have
 * nowhere else to go — editing the sponsor, the upload link, where they are in
 * the journey, and the activity timeline — and Review is the approval screen.
 *
 * This header used to say the prototype's chaser toggle was absent because the
 * reminder scheduler was not built. Both are built: the schedule is in
 * RemindersPanel and the toggle with it.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import SponsorLinkPanel from '../components/SponsorLinkPanel';
import RemindersPanel, { type ReminderSchedule } from '../components/RemindersPanel';
import MessageSponsor from '../components/MessageSponsor';
import Review from './Review';
import { T } from '../lib/copyEdit.tsx';

export interface SponsorContact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

export interface LinkState {
  expiresAt: string;
  lastUsedAt: string | null;
  expired: boolean;
}

interface ActivityItem {
  at: string;
  kind: string;
  direction: 'event' | 'out';
  title: string;
  /** Who caused it, or null for anything automated. */
  actor: string | null;
  detail: unknown;
}

/** What the sponsor has delivered, for the Journey panel. Mirrors the counts
 *  the reminder schedule is built from, so the two cannot disagree. */
interface SponsorProgress {
  requested: number;
  submitted: number;
  approved: number;
  needsReview: number;
  fixRequested: number;
}

interface SponsorDetailData {
  eventSponsorId: string;
  sponsorId: string;
  companyName: string;
  website: string | null;
  tierTitle: string | null;
  eventName: string;
  status: 'invited' | 'not_invited';
  /** Set when the sponsorship has been withdrawn, with why if anybody said. */
  withdrawnAt?: string | null;
  withdrawnReason?: string | null;
  addedAt: string;
  priorEvents: number;
  link: LinkState | null;
  chaserPaused: boolean;
  reminders: ReminderSchedule;
  clientName?: string;
  contacts: SponsorContact[];
  activity: ActivityItem[];
  counts: SponsorProgress;
  /** When a reminder last went out, or null if none has. A step on the
   *  journey, and not derivable from the schedule's next date. */
  remindedAt: string | null;
}

/** Activity kinds are stored as slugs so they stay stable; the wording lives
 *  here, where it can change without a migration. */
const ACTIVITY_LABEL: Record<string, string> = {
  sponsor_added: 'Added as sponsor',
  sponsor_updated: 'Details updated',
  link_reissued: 'Upload link replaced',
  logo_uploaded: 'Logo uploaded',
  logo_uploaded_by_staff: 'Logo added by staff',
  review_sent: 'Review sent to sponsor',
  logo_deleted: 'Logo deleted',
  message_sent: 'Message sent',
  logo_reused: 'Logo reused from the library',
  invite_sent: 'Invite sent',
  invite_resent: 'Invite sent again',
  carryover_confirmed: 'Sponsor confirmed reusing artwork on file',
  /* The sponsor tried to send something and the scan said no. Worth a line of
     its own: without it, a refused upload looked identical to no upload, both
     to whoever was chasing the sponsor and to whoever the sponsor complained
     to about a file that would not go through. */
  upload_refused: 'Upload refused by the security scan',
  sponsor_withdrawn: 'Marked as withdrawn',
  sponsor_reinstated: 'Reinstated',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function SponsorDetail() {
  const { clientId, eventId, eventSponsorId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'review' ? 'review' : 'overview';
  const [data, setData] = useState<SponsorDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [confirmingInvite, setConfirmingInvite] = useState(false);
  const [testing, setTesting] = useState(false);

  const base = `/api/clients/${clientId}/events/${eventId}/sponsors/${eventSponsorId}`;

  const load = useCallback(async () => {
    try {
      const res = await fetch(base);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { sponsor: SponsorDetailData };
      setData(body.sponsor);
    } catch {
      setError('Could not load this sponsor.');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Sends the invite from this page.
   *
   * The sponsors table's Invite action has always pointed here, and there was
   * nothing here to press. A sponsor whose first invite failed to send had no
   * route back from either screen.
   */
  async function invite() {
    setInviting(true);
    setNotice(null);
    setConfirmingInvite(false);
    try {
      const res = await fetch(`${base}/invite`, { method: 'POST' });
      const body = (await res.json().catch(() => ({}))) as {
        sent?: boolean;
        carriedOver?: number;
        replacedOpenedLink?: boolean;
        error?: string;
      };

      if (!res.ok) {
        setNotice(body.error ?? 'That invite did not send.');
        return;
      }

      const carried =
        body.carriedOver && body.carriedOver > 0
          ? ` ${body.carriedOver} version${body.carriedOver === 1 ? '' : 's'} filled in from the library.`
          : '';

      /* Said even on success. Staff need to know the sponsor's old link just
         stopped working, because the sponsor is about to find out. */
      const replaced = body.replacedOpenedLink
        ? ' Their previous link has stopped working, so anyone using it will need the new one.'
        : '';

      setNotice(
        body.sent
          ? `Invite sent.${carried}${replaced}`
          : 'The invite could not be emailed. Use the upload link below to send it yourself.',
      );
      await load();
    } catch {
      setNotice('That invite did not send. Check your connection and try again.');
    } finally {
      setInviting(false);
    }
  }


  /**
   * Emails the signed-in user the invitation this sponsor would receive.
   *
   * There was no test send anywhere in the product, so the only way to check
   * what a sponsor gets was to send them something. Message Sponsor was being
   * used for it, which is a different email that deliberately carries neither
   * the event's notes nor a portal link.
   */
  async function sendTest() {
    setTesting(true);
    setNotice(null);
    try {
      const res = await fetch(`${base}/test`, { method: 'POST' });
      const body = (await res.json().catch(() => ({}))) as {
        sentTo?: string;
        note?: string;
        error?: string;
      };
      setNotice(
        res.ok
          ? `Test invitation sent to ${body.sentTo}. ${body.note ?? ''}`.trim()
          : (body.error ?? 'That test could not be sent.'),
      );
    } catch {
      setNotice('That test could not be sent. Check your connection and try again.');
    } finally {
      setTesting(false);
    }
  }

  if (error) return <p className="auth-error">{error}</p>;
  if (!data) return <div className="app-loading" aria-busy="true" />;

  /**
   * Whether pressing the invite button needs a warning first.
   *
   * ONLY WHEN THE SPONSOR HAS ACTUALLY OPENED THEIR LINK. Re-inviting mints a
   * new one and revokes the old, which for the ordinary case — an invite that
   * never sent — costs nothing, and a confirmation there would be a dialog
   * asking permission to fix a problem. Once the link has been opened, someone
   * may be part-way through uploading, and QA found exactly that: a sponsor
   * logged out mid-upload, with no warning to staff and no explanation to them.
   */
  const inviteReplacesLiveLink = Boolean(data.link && !data.link.expired && data.link.lastUsedAt);

  return (
    <>
      <Link className="back" to={`/c/${clientId}/events/${eventId}`}>
        <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Sponsors
      </Link>

      <div className="ehead">
        <div>
          <h1 className="title">{data.companyName}</h1>
          <div className="emeta">
            {data.tierTitle && <span>{data.tierTitle}</span>}
            <span>{data.eventName}</span>
            {/* This pill answers "have we written to them", NOT "how far along
                are they" — which is what the sponsors list badge answers. Both
                used to be able to say "Invited", so a fully approved sponsor
                read "Approved" in the list and "Invited" on their own page.
                Same word, two questions. Worded as the question it answers. */}
            {data.withdrawnAt ? (
              /* Withdrawn outranks the invite question: it is the first thing
                 anybody opening this page needs to know, and the list already
                 says it. */
              <span className="badge b-neutral" title={data.withdrawnReason ?? undefined}>
                <span className="d" />
                Withdrawn{data.withdrawnReason ? ` · ${data.withdrawnReason}` : ''}
              </span>
            ) : (
              <span className={`badge ${data.status === 'invited' ? 'b-info' : 'b-neutral'}`}>
                <span className="d" />
                {data.status === 'invited' ? 'Invite sent' : 'Not invited yet'}
              </span>
            )}
          </div>
        </div>
        <div className="pactions">
          {/* Primary while they have never been invited, because that is then
              the only thing anybody wants to do on this page. Available after
              that too, as a re-send: it mints a fresh link, which is the only
              option open to us since we store a hash and cannot resend a link
              we can no longer read. */}
          <button
            type="button"
            className={`btn-compact${data.status === 'not_invited' ? '' : ' btn-compact--quiet'}`}
            disabled={inviting}
            onClick={() => {
              /* Straight through unless somebody is using the current link. */
              if (inviteReplacesLiveLink) setConfirmingInvite(true);
              else void invite();
            }}
          >
            {inviting
              ? 'Sending…'
              : data.status === 'not_invited'
                ? 'Send Invite'
                : 'Send Invite Again'}
          </button>
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            disabled={testing}
            onClick={() => void sendTest()}
          >
            {testing ? 'Sending…' : 'Email Me a Test'}
          </button>
          {!messaging && (
            /* Quiet while the invite is the primary action, so the header has
               exactly one filled button in either state. */
            <button
              type="button"
              className={`btn-compact${data.status === 'not_invited' ? ' btn-compact--quiet' : ''}`}
              onClick={() => setMessaging(true)}
            >
              Message Sponsor
            </button>
          )}
        </div>
      </div>

      {/* The warning QA found missing. Re-inviting revokes the link the sponsor
          is holding, and the Upload Link panel already spells that consequence
          out before it acts; this button did the same thing silently. */}
      {confirmingInvite && (
        <div className="notice notice--danger">
          <b>Send a new invite to {data.companyName}?</b>
          <p className="fdesc" style={{ margin: '6px 0 10px' }}>
            They have already opened their upload link. Sending again issues a new one and stops
            the old one working, so anyone part-way through uploading will be signed out and will
            need the new email. Anything already uploaded is kept.
          </p>
          <div className="handoff">
            <button
              type="button"
              className="btn-compact btn-compact--quiet"
              disabled={inviting}
              onClick={() => setConfirmingInvite(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-compact"
              disabled={inviting}
              onClick={() => void invite()}
            >
              {inviting ? 'Sending…' : 'Send It Anyway'}
            </button>
          </div>
        </div>
      )}

      {notice && <p className="save-note">{notice}</p>}

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'overview' ? ' active' : ''}`}
          onClick={() => {
            params.delete('tab');
            setParams(params, { replace: true });
          }}
        >
          Overview
        </button>
        <button
          type="button"
          className={`tab${tab === 'review' ? ' active' : ''}`}
          onClick={() => {
            params.set('tab', 'review');
            setParams(params, { replace: true });
          }}
        >
          Review Logos</button>
      </div>

      {messaging ? (
        <MessageSponsor
          base={base}
          companyName={data.companyName}
          eventName={data.eventName}
          clientName={data.clientName ?? 'the client'}
          contacts={data.contacts}
          onCancel={() => setMessaging(false)}
          onSent={(held) => {
            setMessaging(false);
            setNotice(
              held
                ? 'Message sent. Automatic reminders are held for a week.'
                : 'Message sent.',
            );
            void load();
          }}
        />
      ) : tab === 'review' ? (
        <Review
          clientId={clientId ?? ''}
          eventId={eventId ?? ''}
          eventSponsorId={eventSponsorId ?? ''}
        />
      ) : (
      <div className="sd-grid">
        <section className="panel">
          <div className="panel-head">
            <h3><T k="sponsorDetail.activity.title" d="Activity &amp; Communications" /></h3>
            <span className="hint">the app is the system of record</span>
          </div>
          <div className="panel-body">
            {data.activity.length === 0 ? (
              <p className="fdesc" style={{ margin: 0 }}>
                Nothing has happened yet.
              </p>
            ) : (
              <div className="tline">
                {data.activity.map((a, i) => (
                  <div key={`${a.at}-${i}`} className="tl-item">
                    <span className={`tl-node ${a.direction === 'out' ? 'out' : 'milestone'}`}>
                      {a.direction === 'out' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 6-10 7L2 6" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <path d="M17 8l-5-5-5 5M12 3v12" />
                        </svg>
                      )}
                    </span>
                    <div className="tl-head">
                      <span className="tl-title">
                        {ACTIVITY_LABEL[a.kind] ?? a.title}
                        {/* WHO, which the timeline did not say. "Message sent"
                            and "Message sent by Kregg" answer different
                            questions, and the second is the one that matters
                            when working out whether somebody has already
                            handled this sponsor by hand. */}
                        <span className="who">
                          {a.actor ? ` · by ${a.actor}` : a.direction === 'out' ? ' · automated' : ''}
                        </span>
                      </span>
                      <span className="tl-time">{formatWhen(a.at)}</span>
                    </div>
                    <ActivityBody item={a} />
                    {/* "Delivered and ignored" and "never arrived" are
                        different problems with different fixes. */}
                    <DeliveryChip detail={a.detail} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <div>
          <div className="panel">
            <div className="panel-head">
              <h3><T k="common.panel.details" d="Details" /></h3>
              {!editing && (
                <button type="button" className="cedit" onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
            </div>
            <div className="panel-body">
              {editing ? (
                <EditSponsor
                  base={base}
                  data={data}
                  onCancel={() => setEditing(false)}
                  onSaved={() => {
                    setEditing(false);
                    void load();
                  }}
                />
              ) : (
                <ViewSponsor data={data} />
              )}
            </div>
          </div>

          {/* Where they are stuck, which the progress bar on the sponsors
              table does not answer. Above the link and reminder machinery,
              because it is what somebody opening this page looks at first. */}
          <Journey data={data} />

          <SponsorLinkPanel
            base={base}
            companyName={data.companyName}
            link={data.link}
            onIssued={() => void load()}
          />

          <RemindersPanel
            base={base}
            schedule={data.reminders}
            withdrawn={Boolean(data.withdrawnAt)}
            onChanged={() => void load()}
            onMessage={() => setMessaging(true)}
          />
        </div>
      </div>
      )}
    </>
  );
}

function ViewSponsor({ data }: { data: SponsorDetailData }) {
  const primary = data.contacts.find((c) => c.isPrimary);
  const others = data.contacts.filter((c) => !c.isPrimary);

  return (
    <>
      <ul className="metalist">
        <li>
          <span className="k"><T k="common.field.tier" d="Tier / Title" /></span>
          <span className="v">{data.tierTitle ?? '—'}</span>
        </li>
        <li>
          <span className="k"><T k="common.field.website" d="Website" /></span>
          <span className="v">{data.website ?? '—'}</span>
        </li>
        <li>
          <span className="k"><T k="sponsorDetail.field.history" d="History" /></span>
          {/* Counted from the company's other sponsorships rather than stored,
              so it cannot drift from the events that actually exist. */}
          <span className="v">
            {data.priorEvents === 0
              ? 'New this year'
              : `Returning · ${data.priorEvents} prior event${data.priorEvents === 1 ? '' : 's'}`}
          </span>
        </li>
      </ul>

      <p className="sd-lbl"><T k="common.field.primaryContact" d="Primary Contact" /></p>
      <ul className="metalist">
        {primary ? (
          <li>
            <span className="k">{primary.name}</span>
            <span className="v">{primary.email}</span>
          </li>
        ) : (
          <li>
            <span className="k"><T k="sponsorDetail.contact.none" d="None set" /></span>
            <span className="v">—</span>
          </li>
        )}
      </ul>

      {others.length > 0 && (
        <>
          <p className="sd-lbl"><T k="sponsorDetail.contact.additional" d="Additional contacts" /></p>
          <ul className="metalist">
            {others.map((c) => (
              <li key={c.id}>
                <span className="k">{c.name}</span>
                <span className="v">{c.email}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function EditSponsor({
  base,
  data,
  onCancel,
  onSaved,
}: {
  base: string;
  data: SponsorDetailData;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [companyName, setCompanyName] = useState(data.companyName);
  const [website, setWebsite] = useState(data.website ?? '');
  const [tierTitle, setTierTitle] = useState(data.tierTitle ?? '');

  const existingPrimary = data.contacts.find((c) => c.isPrimary);
  const [primary, setPrimary] = useState({
    name: existingPrimary?.name ?? '',
    email: existingPrimary?.email ?? '',
  });
  const [extra, setExtra] = useState(
    data.contacts.filter((c) => !c.isPrimary).map((c) => ({ name: c.name, email: c.email })),
  );

  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const renamed = companyName.trim() !== data.companyName;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setProblem(null);

    const res = await fetch(base, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        companyName: companyName.trim(),
        website: website.trim() || null,
        tierTitle: tierTitle.trim() || null,
        contacts: [
          { name: primary.name.trim(), email: primary.email.trim(), isPrimary: true },
          ...extra
            .filter((c) => c.name.trim() && c.email.trim())
            .map((c) => ({ name: c.name.trim(), email: c.email.trim(), isPrimary: false })),
        ],
      }),
    });

    setSaving(false);

    if (res.ok) {
      onSaved();
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { error?: string };
    setProblem(body.error ?? 'That did not save.');
  }

  return (
    <form onSubmit={submit}>
      <div className="fg">
        <label className="fl" htmlFor="companyName"><T k="common.field.companyName" d="Company Name" /></label>
        <input
          id="companyName"
          className="fi"
          required
          maxLength={200}
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
        {/* The company row is shared across every event it has sponsored, which
            is what keeps its logo history together. Renaming is allowed, but
            not quietly. */}
        {renamed && (
          <p className="fwarn">
            This renames the company everywhere, including past events and the logo library.
          </p>
        )}
      </div>

      <div className="fg">
        <label className="fl" htmlFor="website"><T k="common.field.website" d="Website" /></label>
        <input
          id="website"
          className="fi"
          maxLength={300}
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="fg">
        <label className="fl" htmlFor="tierTitle"><T k="common.field.tier" d="Tier / Title" /></label>
        <input
          id="tierTitle"
          className="fi"
          maxLength={200}
          value={tierTitle}
          onChange={(e) => setTierTitle(e.target.value)}
        />
        <p className="fdesc"><T k="sponsorDetail.form.tierHint" d="This event only. Tiers are re-negotiated each year." /></p>
      </div>

      <div className="fg">
        <label className="fl"><T k="common.field.primaryContact" d="Primary Contact" /></label>
        <div className="fetch-row">
          <input
            className="fi"
            required
            placeholder="Name"
            aria-label="Primary contact name"
            value={primary.name}
            onChange={(e) => setPrimary((p) => ({ ...p, name: e.target.value }))}
          />
          <input
            className="fi"
            type="email"
            required
            placeholder="Email"
            aria-label="Primary contact email"
            value={primary.email}
            onChange={(e) => setPrimary((p) => ({ ...p, email: e.target.value }))}
          />
        </div>
      </div>

      <div className="fg">
        <label className="fl"><T k="sponsorDetail.form.additional" d="Additional Contacts" /></label>
        {extra.map((c, i) => (
          <div key={i} className="fetch-row" style={{ marginBottom: 8 }}>
            <input
              className="fi"
              placeholder="Name"
              aria-label={`Additional contact ${i + 1} name`}
              value={c.name}
              onChange={(e) =>
                setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
              }
            />
            <input
              className="fi"
              type="email"
              placeholder="Email"
              aria-label={`Additional contact ${i + 1} email`}
              value={c.email}
              onChange={(e) =>
                setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))
              }
            />
            <button
              type="button"
              className="btn-compact btn-compact--quiet"
              onClick={() => setExtra((prev) => prev.filter((_, j) => j !== i))}
              aria-label={`Remove additional contact ${i + 1}`}
            >
              <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-compact btn-compact--quiet"
          onClick={() => setExtra((prev) => [...prev, { name: '', email: '' }])}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Another Contact
        </button>
        <p className="fdesc">
          Everyone here is CC'd on the sponsor's emails and can pick things up if the primary goes
          quiet.
        </p>
      </div>

      {problem && <p className="auth-error">{problem}</p>}

      <div className="factions">
        <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-compact" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

/** What happened to an outbound message, when it is worth saying. A delivered
 *  message needs no chip; a bounced one is the whole story. */
/**
 * Delivery state, as separate facts rather than one sentence.
 *
 * DELIVERED AND OPENED ARE DIFFERENT THINGS and the difference decides what to
 * do next. A reminder that arrived and was never opened means the address works
 * and the person is not reading; chasing harder is the wrong answer and a phone
 * call is the right one. One collapsed "Delivered and opened" string hid that,
 * which is why the prototype draws two chips.
 */
function DeliveryChip({ detail }: { detail: unknown }) {
  const info = detail as {
    status?: string;
    failureDetail?: string | null;
    delivered?: boolean;
    opened?: boolean;
  } | null;
  const status = info?.status;

  if (!status || status === 'queued') return null;

  if (status === 'bounced' || status === 'failed') {
    return (
      <div className="tl-chips">
        <span className="dchip dchip--bad">
          <span className="dd" />
          {status === 'bounced' ? 'Bounced' : 'Failed to send'}
          {info?.failureDetail ? ` · ${info.failureDetail}` : ''}
        </span>
      </div>
    );
  }

  /* Postmark tells us about delivery and opens through the webhook, and a
     message can sit as "sent" for a while before either arrives. Absence is
     reported as nothing rather than as a negative: "not opened" a second after
     sending would be noise. */
  if (!info?.delivered && !info?.opened) return null;

  return (
    <div className="tl-chips">
      {info.delivered && (
        <span className="dchip dchip--ok">
          <span className="dd" />
          Delivered
        </span>
      )}
      {info.opened && (
        <span className="dchip dchip--open">
          <span className="dd" />
          Opened
        </span>
      )}
    </div>
  );
}

/**
 * The one line under a timeline entry that says what actually happened.
 *
 * The entries used to be a title and a time and nothing else, so "Logo
 * uploaded" did not say what, "Message sent" did not say to whom, and
 * "Logo reused from the library" did not say which slot it filled. The
 * prototype's `tl-body` carries that, and every value here is already on the
 * activity row's own detail.
 */
function ActivityBody({ item }: { item: ActivityItem }) {
  const d = item.detail as Record<string, unknown> | null;
  if (!d) return null;

  const text = (() => {
    if (item.direction === 'out') {
      const to = typeof d.toEmail === 'string' ? d.toEmail : null;
      /* The subject IS the title for an email, so the body says who it went
         to rather than repeating it. */
      return to ? `Sent to ${to}` : null;
    }
    if (typeof d.filename === 'string') return d.filename;
    if (typeof d.carriedOver === 'number' && d.carriedOver > 0) {
      return `${d.carriedOver} version${d.carriedOver === 1 ? '' : 's'} filled in from the library`;
    }
    if (typeof d.tierTitle === 'string') return d.tierTitle;
    if (typeof d.revokedPrevious === 'boolean' && d.revokedPrevious) {
      return 'Any earlier link stopped working';
    }
    return null;
  })();

  return text ? <div className="tl-body">{text}</div> : null;
}

/**
 * How far along this sponsor is, as five fixed steps.
 *
 * WHY FIXED STEPS RATHER THAN A PERCENTAGE. The progress bar on the sponsors
 * table already answers "how much is done". This answers "where are they
 * stuck", which is a different question and the one somebody opening a sponsor
 * usually has. A sponsor sitting at "Reminded" for three weeks and one sitting
 * at "In review" for three weeks need completely different things from you.
 *
 * Derived rather than stored. Every step reads from what has actually
 * happened, so the panel cannot drift from the sponsor's real state the way a
 * status column would.
 */
function Journey({ data }: { data: SponsorDetailData }) {
  const c = data.counts;
  const invited = data.status === 'invited';
  const reminded = data.remindedAt !== null;
  const uploaded = c.submitted > 0;
  const inReview = c.needsReview > 0;
  const approved = c.requested > 0 && c.approved >= c.requested;

  /* Ordered, and each step is "done" once anything after it has happened: a
     sponsor whose logo is approved was necessarily invited, even if the invite
     predates this record. `current` is the first thing not yet done. */
  const steps = [
    { label: 'Invited', done: invited || uploaded || approved },
    { label: 'Reminded', done: reminded },
    { label: 'Logo uploaded', done: uploaded || approved },
    { label: 'In review', done: approved, active: inReview },
    { label: 'Approved', done: approved },
  ];

  const currentIndex = steps.findIndex((s) => !s.done);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><T k="sponsorDetail.journey.title" d="Journey" /></h3>
      </div>
      <div className="panel-body">
        <div className="journey">
          {steps.map((s, i) => (
            <div
              key={s.label}
              className={`jstep${s.done ? ' done' : ''}${
                !s.done && (i === currentIndex || s.active) ? ' current' : ''
              }`}
            >
              <span className="jd">
                {s.done && (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </span>
              <span className="jl">{s.label}</span>
            </div>
          ))}
        </div>
        {/* Skipped steps are normal and worth saying so. A sponsor who
            uploaded the day they were invited never needed reminding, and a
            greyed "Reminded" above a done "Logo uploaded" would otherwise look
            like something went wrong. */}
        {!reminded && (uploaded || approved) && (
          <p className="fdesc" style={{ marginTop: 'var(--sp3)' }}>
            No reminder was needed.
          </p>
        )}
      </div>
    </div>
  );
}
